// client/src/coms/organisation/ExternalBrowser.js
// Browsing one connected library (Google Drive, Dropbox, OneDrive, GitHub, GitLab or S3). Listings come live
// from the provider with the provider's own permissions; Voxemera keeps only stable reference ids (and, for
// libraries shared through an administrator's connection, a bounded metadata cache for search/offline use).
// Uploads go straight from the browser to the provider. Provider-side deletion is a separate, confirmed action.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiGetJson, apiPostJson, apiPatchJson, apiPut, apiDelete, directUpload, uploadForm } from './orgApi';
import { formatBytes, formatDate, ProviderBadge, kindIcon, notify, Dialog, useConfirm, usePrompt, Spinner, EmptyState, ErrorNote, PROVIDERS } from './orgShared';
import { saveBookmark } from './bookmarkActions';
import { ItemRow, Breadcrumbs, statusLabel } from './libraryParts';
import { connectWithFeedback, pickDriveFiles, downloadActions, openWhenReady } from './storageAccounts';

const VISIBILITY_TEXT = {
  library: 'Everyone who can see this library',
  roles: 'Only members with selected roles',
  private: 'Only me',
};

export default function ExternalBrowser({ base, source, onChanged, initialItem, canShareResource = false }) {
  const p = PROVIDERS[source.provider] || {};
  // "Member links" Drive libraries: members link files from their own Drive, each link with its own visibility.
  const memberLinks = source.access_model === 'member_links';
  const [linkDialog, setLinkDialog] = useState(null); // { mode: 'add' } | { mode: 'edit', item }
  const [shareDialog, setShareDialog] = useState(null);
  const [stack, setStack] = useState([{ id: '', name: source.root_label || source.name }]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [details, setDetails] = useState(initialItem ? { vox_id: Number(initialItem) } : null);
  const [uploads, setUploads] = useState([]);
  const [commitFile, setCommitFile] = useState(null);
  const [sharing, setSharing] = useState(null);
  const fileInput = useRef(null);
  const [confirm, confirmEl] = useConfirm();
  const [prompt, promptEl] = usePrompt();
  const cur = stack[stack.length - 1];
  const caps = (data && data.capabilities) || source.capabilities || {};

  const load = useCallback(
    async (cursor) => {
      setErr(null);
      try {
        const qs = new URLSearchParams({ ...(cur.id ? { ref: cur.id } : {}), ...(cursor ? { cursor } : {}) });
        const r = await apiGetJson(`${base}/library/sources/${source.id}/browse?${qs}`);
        setData((prev) => (cursor && prev ? { ...r, items: [...prev.items, ...r.items] } : r));
      } catch (e) {
        setErr(e);
        if (!cursor) setData(null);
      }
    },
    [base, source.id, cur.id]
  );
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      return undefined;
    }
    const t = setTimeout(() => {
      apiGetJson(`${base}/library/sources/${source.id}/search?q=${encodeURIComponent(q.trim())}`).then((r) => setResults(r.items), (e) => setResults({ error: e }));
    }, 400);
    return () => clearTimeout(t);
  }, [q, base, source.id]);

  // Your own account for a "personal accounts" library: connect one, or reconnect the one that expired (in place,
  // same account). A shared library's connection is reconnected by the administrator who made it.
  async function connectMine() {
    const pc = source.personal_connection;
    const r = await connectWithFeedback(source.provider, p.label, { reconnect: pc && pc.needs_reconnect ? pc.id : null });
    if (r.ok) {
      onChanged && onChanged();
      load();
    }
  }
  async function reconnectShared() {
    const r = await connectWithFeedback(source.provider, p.label, { reconnect: source.connection_id });
    if (r.ok) {
      onChanged && onChanged();
      load();
    }
  }
  async function chooseAccount(id) {
    try {
      await apiPut(`${base}/library/sources/${source.id}/my-account`, { connection_id: id ? Number(id) : null });
      onChanged && onChanged();
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }
  // Google's file picker: the chosen files become available to Voxemera (drive.file) and are added here. In a
  // member-links library the member first says who in the server may see the links.
  async function pickFromDrive(visibility) {
    try {
      const grant = await apiPostJson(`${base}/library/sources/${source.id}/picker`, {});
      const ids = await pickDriveFiles(grant, { title: memberLinks ? `Choose files to link in ${source.name}` : undefined });
      if (!ids.length) return;
      const r = await apiPostJson(`${base}/library/sources/${source.id}/picked`, { ids, ...(visibility || {}) });
      notify(`${memberLinks ? 'Linked' : 'Added'} ${r.items.length} file${r.items.length === 1 ? '' : 's'} from Google Drive${r.refused ? ` (${r.refused} could not be opened with your account)` : ''}`, { tone: 'success' });
      load();
      onChanged && onChanged();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function changeVisibility(it, visibility) {
    try {
      await apiPatchJson(`${base}/library/sources/${source.id}/links/${it.vox_id}`, visibility);
      notify('Updated who can see this link', { tone: 'success' });
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function removeLink(it) {
    const mine = it.link && it.link.mine;
    const ok = await confirm({
      title: `Remove “${it.name}” from ${source.name}?`,
      body: `${mine ? 'Your link' : 'This link'} disappears from the library. The file stays in ${mine ? 'your' : 'its owner’s'} Google Drive exactly as it is; nothing is deleted there.`,
      confirmLabel: 'Remove link',
    });
    if (!ok) return;
    try {
      const r = await apiDelete(`${base}/library/sources/${source.id}/items/${it.vox_id}`);
      notify(r.note, { tone: 'success' });
      load();
      onChanged && onChanged();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  function openLinkDownload(it, d) {
    openWhenReady(apiGetJson(`${base}/library/sources/${source.id}/items/${it.vox_id}/download?format=json&export=${encodeURIComponent(d.format)}`)).catch((e) => notify(e.message, { tone: 'error' }));
  }

  function upload(files) {
    for (const file of Array.from(files || [])) {
      if (caps.writeNeedsMessage) {
        setCommitFile(file);
        return;
      }
      const id = `${Date.now()}-${file.name}`;
      setUploads((u) => [...u, { id, name: file.name, progress: 0 }]);
      (async () => {
        try {
          const s = await apiPostJson(`${base}/library/sources/${source.id}/uploads`, { name: file.name, size: file.size, mime: file.type || 'application/octet-stream', parent_ref: cur.id || '' });
          const job = directUpload(s.upload, file, (pr) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: pr } : x))));
          setUploads((u) => u.map((x) => (x.id === id ? { ...x, abort: () => { job.abort(); apiDelete(`${base}/library/sources/${source.id}/uploads/${s.token}`).catch(() => {}); } } : x)));
          const result = await job.promise;
          await apiPostJson(`${base}/library/sources/${source.id}/uploads/${s.token}/complete`, { result });
          setUploads((u) => u.filter((x) => x.id !== id));
          notify(`Uploaded ${file.name} to ${p.label}`, { tone: 'success' });
          load();
        } catch (e) {
          setUploads((u) => u.map((x) => (x.id === id ? { ...x, error: e.cancelled ? 'Cancelled' : e.message } : x)));
          setTimeout(() => setUploads((u) => u.filter((x) => x.id !== id)), 9000);
        }
      })();
    }
  }

  async function mkdir() {
    const name = await prompt({ title: `New folder in ${p.label}`, label: 'Folder name', confirmLabel: 'Create' });
    if (!name) return;
    try {
      await apiPostJson(`${base}/library/sources/${source.id}/folders`, { name, parent_ref: cur.id || '' });
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function rename(it) {
    const name = await prompt({ title: `Rename in ${p.label}`, label: 'Name', value: it.name });
    if (!name || name === it.name) return;
    try {
      await apiPatchJson(`${base}/library/sources/${source.id}/items/${it.vox_id}`, { name });
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function removeAtProvider(it) {
    const ok = await confirm({
      title: `Delete “${it.name}” in ${p.label}?`,
      body: `This deletes the item at ${p.label} itself (moved to its trash or recycle bin where the provider has one). This is different from removing it from Voxemera’s index.`,
      confirmLabel: `Delete in ${p.label}`,
      danger: true,
    });
    if (!ok) return;
    try {
      await apiDelete(`${base}/library/sources/${source.id}/items/${it.vox_id}?scope=provider`, { confirm: 'delete-at-provider' });
      notify(`Deleted in ${p.label}`, { tone: 'success' });
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function forget(it) {
    try {
      const r = await apiDelete(`${base}/library/sources/${source.id}/items/${it.vox_id}`);
      notify(r.note, { tone: 'success' });
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function download(it) {
    try {
      const r = await apiGetJson(`${base}/library/sources/${source.id}/items/${it.vox_id}/download?format=json`);
      if (r && r.url) window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      if (e.data && e.data.code === 'open_in_provider') return openInProvider(it);
      if (e.status === 404 || e.status === undefined) {
        // Streamed downloads (private repositories) answer with the file itself.
        window.location.href = `/api/coms${base}/library/sources/${source.id}/items/${it.vox_id}/download`;
        return;
      }
      notify(e.message, { tone: 'error' });
    }
  }

  function openInProvider(it) {
    if (memberLinks) {
      openWhenReady(apiGetJson(`${base}/library/sources/${source.id}/items/${it.vox_id}/open?format=json`)).catch((e) => {
        notify(e.message, { tone: 'error' });
        if (e.status === 410) load();
      });
      return;
    }
    if (it.web_url) window.open(it.web_url, '_blank', 'noopener,noreferrer');
    else window.open(`/api/coms${base}/library/sources/${source.id}/items/${it.vox_id}/open`, '_blank', 'noopener,noreferrer');
  }

  const code = err && err.data && err.data.code;
  const items = results && Array.isArray(results) ? results : data ? data.items : [];
  const streamed = ['github', 'gitlab'].includes(source.provider) && source.access_model === 'shared_connection';

  return (
    <div
      className="vxo-browser"
      onDragOver={(e) => {
        if (caps.upload && Array.from(e.dataTransfer.types || []).includes('Files')) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!caps.upload) return;
        e.preventDefault();
        upload(e.dataTransfer.files);
      }}
      data-testid="external-browser"
    >
      <div className="vxo-toolbar">
        <div className="vxo-toolbar-title">
          <h2>
            <ProviderBadge provider={source.provider} withLabel /> {source.name}
          </h2>
          <span className="vxo-muted">{source.access_note}</span>
        </div>
        <div className="vxo-toolbar-actions">
          <input className="vxo-input" type="search" placeholder={`Search ${p.label}`} aria-label={`Search ${p.label}`} value={q} onChange={(e) => setQ(e.target.value)} />
          {source.picker ? (
            <button type="button" className="vx-btn primary" onClick={() => (memberLinks ? setLinkDialog({ mode: 'add' }) : pickFromDrive())} data-testid="library-picker">
              {memberLinks ? 'Link files from Google Drive' : 'Add files from Google Drive'}
            </button>
          ) : memberLinks && data && data.can_link && !data.has_account ? (
            <button type="button" className="vx-btn" onClick={connectMine} data-testid="library-connect-drive">
              Connect Google Drive to link files
            </button>
          ) : null}
          {caps.mkdir ? (
            <button type="button" className="vx-btn" onClick={mkdir}>
              New folder
            </button>
          ) : null}
          {caps.upload ? (
            <>
              <button type="button" className="vx-btn primary" onClick={() => fileInput.current && fileInput.current.click()}>
                {caps.writeNeedsMessage ? 'Commit a file' : `Upload to ${p.label}`}
              </button>
              <input ref={fileInput} type="file" multiple={!caps.writeNeedsMessage} hidden onChange={(e) => {
                upload(e.target.files);
                e.target.value = '';
              }} />
            </>
          ) : null}
          <button type="button" className="vx-icon-btn" onClick={() => load()} aria-label="Refresh" title="Refresh">
            ↻
          </button>
        </div>
      </div>
      <Breadcrumbs crumbs={stack} onGo={(_id, i) => setStack(stack.slice(0, i + 1))} />
      <p className="vxo-where">
        <ProviderBadge provider={source.provider} /> {source.where} {source.mode === 'readwrite' ? '' : 'Connected read-only.'}
      </p>
      {source.status !== 'ok' && !err ? <div className="vxo-warn">{statusLabel(source.status)}{source.status_detail ? ` — ${source.status_detail}` : ''}</div> : null}
      {source.my_accounts && source.my_accounts.length > 1 ? (
        <label className="vxo-field vxo-account-choice">
          <span>Your {p.label} account for this library</span>
          <select value={source.personal_connection ? String(source.personal_connection.id) : ''} onChange={(e) => chooseAccount(e.target.value)} data-testid="library-account-choice">
            {source.my_accounts.map((a) => (
              <option key={a.id} value={String(a.id)}>
                {a.label}
                {a.status !== 'ok' ? ' (needs reconnecting)' : ''}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {data && data.virtual_root && !cur.id ? <p className="vxo-hint">Shows the Google Drive files that were picked with Google’s file picker or created through Voxemera. The rest of the Drive stays private.</p> : null}
      {data && data.stale ? <div className="vxo-warn">{data.message} Showing the last synced list.</div> : null}
      {code === 'personal_connection_required' ? (
        <EmptyState icon="🔑" title={`Connect your ${p.label} account`}>
          <p>This library uses each member’s own {p.label} account, so {p.label} decides what you can see. Voxemera never shares one person’s access with another.</p>
          <button type="button" className="vx-btn primary" onClick={connectMine} data-testid="connect-personal">
            Connect {p.label}
          </button>
        </EmptyState>
      ) : code === 'auth' ? (
        <EmptyState icon="⚠️" title="This connection needs to be renewed">
          <p>{err.message}</p>
          {source.access_model === 'personal_accounts' ? (
            <button type="button" className="vx-btn primary" onClick={connectMine} data-testid="reconnect-personal">
              Reconnect {p.label}
            </button>
          ) : source.can_manage && source.connection_id && p.label && source.provider !== 's3' ? (
            <button type="button" className="vx-btn primary" onClick={reconnectShared}>
              Reconnect the server’s {p.label} account
            </button>
          ) : (
            <p className="vxo-muted">A server administrator needs to reconnect it (Storage &amp; connections).</p>
          )}
        </EmptyState>
      ) : (
        <ErrorNote error={err} onRetry={() => load()} />
      )}
      {uploads.length ? (
        <ul className="vxo-uploads" aria-label="Uploads">
          {uploads.map((u) => (
            <li key={u.id} className={u.error ? 'is-error' : ''}>
              <span className="vxo-upload-name">
                {u.name} → {p.label}
              </span>
              {u.error ? <span className="vxo-upload-err">{u.error}</span> : <progress max={1} value={u.progress} aria-label="Upload progress" />}
              {!u.error && u.abort ? (
                <button type="button" className="vx-btn small" onClick={u.abort}>
                  Cancel
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {!data && !err ? <Spinner label={`Loading from ${p.label}…`} /> : null}
      {results && results.error ? <ErrorNote error={results.error} /> : null}
      {memberLinks && data && !results ? (
        <p className="vxo-hint" data-testid="member-links-hint">
          Linked files stay in their owners’ Google Drive; Voxemera stores only names and links. Seeing a link here doesn’t mean you can open the file: Google Drive checks your own access when you open it.
          {data.can_link ? '' : ' You can’t add links to this library.'}
        </p>
      ) : null}
      {data && !items.length ? <EmptyState icon="📂" title={results ? 'Nothing found' : memberLinks ? 'No linked files yet' : 'This folder is empty'} /> : null}
      <div className="vxo-items is-list" role="list">
        {items.map((it) =>
          memberLinks ? (
            <ItemRow
              key={`${it.vox_id}-${it.id}`}
              testId={`member-link-${it.vox_id}`}
              icon={kindIcon(it.mime, { folder: it.is_folder })}
              provider={source.provider}
              name={it.name}
              badge={it.link.unavailable ? 'Unavailable' : it.link.shared_by_link ? 'Anyone with the link' : undefined}
              meta={
                it.link.unavailable
                  ? 'No longer available in Google Drive (deleted, trashed or unshared there). Remove the link or pick the file again.'
                  : [it.is_folder ? 'Folder' : formatBytes(it.size), `Linked by ${it.link.mine ? 'you' : it.link.linked_by || 'a member'}`, VISIBILITY_TEXT[it.link.visibility]].filter(Boolean).join(' · ')
              }
              onOpen={() => (it.link.unavailable ? null : openInProvider(it))}
              actions={[
                !it.link.unavailable && { label: `Open in ${p.label}`, onClick: () => openInProvider(it) },
                ...(!it.link.unavailable ? downloadActions(it, (d) => openLinkDownload(it, d)) : []),
                { label: 'Save bookmark', onClick: () => saveBookmark('external', it.vox_id) },
                canShareResource && !it.link.unavailable && it.link.visibility === 'library' && { label: 'Share as a resource…', onClick: () => setSharing(it) },
                it.link.can_change && { label: 'Who can see this link…', onClick: () => setLinkDialog({ mode: 'edit', item: it }) },
                it.link.can_change && !it.link.unavailable && { label: 'Sharing in Google Drive…', onClick: () => setShareDialog(it) },
                it.link.can_remove && { label: 'Remove from this library', onClick: () => removeLink(it) },
              ]}
            />
          ) : (
          <ItemRow
            key={`${it.vox_id}-${it.id}`}
            icon={kindIcon(it.mime, { folder: it.is_folder })}
            provider={source.provider}
            name={it.name}
            meta={[it.is_folder ? 'Folder' : formatBytes(it.size), it.owner, it.modified_at ? formatDate(it.modified_at) : null, it.path && results ? it.path : null].filter(Boolean).join(' · ')}
            onOpen={() => (it.is_folder ? (setQ(''), setStack([...stack, { id: it.id, name: it.name }])) : setDetails(it))}
            actions={[
              !it.is_folder && { label: 'Details & history', onClick: () => setDetails(it) },
              { label: `Open in ${p.label}`, onClick: () => openInProvider(it) },
              !it.is_folder && caps.download && { label: 'Download', onClick: () => (streamed ? (window.location.href = `/api/coms${base}/library/sources/${source.id}/items/${it.vox_id}/download`) : download(it)) },
              { label: 'Save bookmark', onClick: () => saveBookmark('external', it.vox_id) },
              canShareResource && { label: 'Share as a resource…', onClick: () => setSharing(it) },
              caps.rename && { label: 'Rename', onClick: () => rename(it) },
              source.can_manage && source.access_model === 'shared_connection' && { label: 'Remove from Voxemera index', onClick: () => forget(it) },
              caps.delete && (source.can_manage || source.access_model === 'personal_accounts') && { label: `Delete in ${p.label}…`, danger: true, onClick: () => removeAtProvider(it) },
            ]}
          />
          )
        )}
      </div>
      {linkDialog ? (
        <LinkVisibilityDialog
          base={base}
          title={linkDialog.mode === 'add' ? `Link files to ${source.name}` : `Who can see “${linkDialog.item.name}”?`}
          confirmLabel={linkDialog.mode === 'add' ? 'Choose files in Google Drive…' : 'Save'}
          initial={linkDialog.item ? { visibility: linkDialog.item.link.visibility, role_ids: linkDialog.item.link.role_ids || [] } : { visibility: 'library', role_ids: [] }}
          onClose={() => setLinkDialog(null)}
          onConfirm={(v) => {
            const d = linkDialog;
            setLinkDialog(null);
            if (d.mode === 'add') pickFromDrive(v);
            else changeVisibility(d.item, v);
          }}
        />
      ) : null}
      {shareDialog ? (
        <DriveSharingDialog
          base={base}
          source={source}
          item={shareDialog}
          onClose={() => setShareDialog(null)}
          onChanged={() => {
            setShareDialog(null);
            load();
          }}
        />
      ) : null}
      {data && data.cursor && !results ? (
        <button type="button" className="vx-btn vxo-more" onClick={() => load(data.cursor)}>
          Load more
        </button>
      ) : null}
      {details ? <ExternalDetails base={base} source={source} item={details} onClose={() => setDetails(null)} onDownload={download} onOpen={openInProvider} /> : null}
      {commitFile ? (
        <CommitDialog
          file={commitFile}
          provider={p.label}
          onClose={() => setCommitFile(null)}
          onCommit={async (message) => {
            try {
              await uploadForm(`${base}/library/sources/${source.id}/commit`, [{ field: 'file', file: commitFile }], { message, parent_ref: cur.id || '' }).promise;
              notify(`Committed ${commitFile.name}`, { tone: 'success' });
              setCommitFile(null);
              load();
            } catch (e) {
              notify(e.message, { tone: 'error' });
            }
          }}
        />
      ) : null}
      {sharing ? <ShareResourceDialog base={base} source={source} item={sharing} onClose={() => setSharing(null)} /> : null}
      {confirmEl}
      {promptEl}
    </div>
  );
}

// Lists a cloud file in the server's Resources. The resource only points at the file: whoever opens it still
// needs access at the provider (or, for "own account" libraries, their own connected account).
function ShareResourceDialog({ base, source, item, onClose }) {
  const p = PROVIDERS[source.provider] || {};
  const [title, setTitle] = useState(item.name || '');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await apiPostJson(`${base}/resources`, { title, description, category: 'reference', target_type: 'external', target_ref: String(item.vox_id) });
      notify('Shared in Resources', { tone: 'success' });
      onClose();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title="Share as a resource"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="vx-btn primary" disabled={busy || !title.trim()} onClick={save} data-testid="share-resource-save">
            Share
          </button>
        </>
      }
    >
      <ErrorNote error={err} />
      <p className="vxo-hint">
        Members see this in the server’s Resources. Opening the file still needs access in {p.label}
        {source.access_model === 'personal_accounts' ? ' with their own account' : ''}; sharing here does not change who can open it there.
      </p>
      <label className="vxo-field">
        <span>Title</span>
        <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="vxo-field">
        <span>Description (optional)</span>
        <textarea value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
      </label>
    </Dialog>
  );
}

function ExternalDetails({ base, source, item, onClose, onDownload, onOpen }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    apiGetJson(`${base}/library/sources/${source.id}/items/${item.vox_id}`).then(setD, setErr);
  }, [base, source.id, item.vox_id]);
  const it = (d && d.item) || item;
  const p = PROVIDERS[source.provider] || {};
  return (
    <Dialog title={it.name || 'File'} onClose={onClose} wide>
      <ErrorNote error={err} />
      {!d && !err ? <Spinner label={`Asking ${p.label}…`} /> : null}
      {d ? (
        <div className="vxo-details">
          <div className="vxo-details-side">
            <dl className="vxo-dl">
              <dt>Stored in</dt>
              <dd>
                <ProviderBadge provider={source.provider} withLabel /> · {source.name}
              </dd>
              {it.path ? (
                <>
                  <dt>Path</dt>
                  <dd>{it.path}</dd>
                </>
              ) : null}
              <dt>Type</dt>
              <dd>{it.mime || '—'}</dd>
              <dt>Size</dt>
              <dd>{formatBytes(it.size)}</dd>
              {it.owner ? (
                <>
                  <dt>Owner</dt>
                  <dd>{it.owner}</dd>
                </>
              ) : null}
              <dt>Modified</dt>
              <dd>{it.modified_at ? formatDate(it.modified_at) : '—'}</dd>
              <dt>Access</dt>
              <dd>{d.where}</dd>
            </dl>
            <div className="vxo-actions">
              <button type="button" className="vx-btn primary" onClick={() => onOpen(it)}>
                Open in {p.label}
              </button>
              {d.capabilities && d.capabilities.download ? (
                <button type="button" className="vx-btn" onClick={() => onDownload(it)}>
                  Download
                </button>
              ) : null}
              {d.edit_url ? (
                <a className="vx-btn" href={d.edit_url} target="_blank" rel="noopener noreferrer">
                  Edit in {p.label}
                </a>
              ) : null}
              <button type="button" className="vx-btn" onClick={() => saveBookmark('external', it.vox_id)}>
                🔖 Save bookmark
              </button>
            </div>
            {Array.isArray(d.versions) ? (
              <>
                <h3 className="vxo-h3">{['github', 'gitlab'].includes(source.provider) ? 'Commit history' : 'Version history'}</h3>
                {!d.versions.length ? <p className="vxo-muted">The provider reports no versions.</p> : null}
                <ol className="vxo-versions">
                  {d.versions.map((v) => (
                    <li key={v.id}>
                      <div>
                        <strong>{v.message || String(v.id).slice(0, 10)}</strong>
                        <div className="vxo-muted">
                          {v.modified_at ? formatDate(v.modified_at) : ''} {v.author ? `· ${v.author}` : ''} {v.size != null ? `· ${formatBytes(v.size)}` : ''}
                        </div>
                      </div>
                      {v.web_url ? (
                        <a className="vx-btn small" href={v.web_url} target="_blank" rel="noopener noreferrer">
                          View
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ol>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

function CommitDialog({ file, provider, onClose, onCommit }) {
  const [message, setMessage] = useState(`Add ${file.name}`);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      title={`Commit ${file.name} to ${provider}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="vx-btn primary"
            disabled={busy || !message.trim()}
            onClick={async () => {
              setBusy(true);
              await onCommit(message.trim());
              setBusy(false);
            }}
          >
            Commit
          </button>
        </>
      }
    >
      <p className="vxo-hint">This creates a commit on the library’s branch with your message. Files up to 1 MB; Voxemera never creates branches or pull requests.</p>
      <label className="vxo-field">
        <span>Commit message</span>
        <input value={message} maxLength={200} onChange={(e) => setMessage(e.target.value)} />
      </label>
    </Dialog>
  );
}

// Who in the server may see a member's Drive link. Seeing a link never gives access to the file itself.
export function LinkVisibilityDialog({ base, title, confirmLabel, initial, onClose, onConfirm }) {
  const [visibility, setVisibility] = useState(initial.visibility || 'library');
  const [roleIds, setRoleIds] = useState(initial.role_ids || []);
  const [roles, setRoles] = useState(null);
  useEffect(() => {
    apiGetJson(`${base}/roles`).then((r) => setRoles((r.roles || r || []).filter((x) => !Number(x.is_default))), () => setRoles([]));
  }, [base]);
  const invalid = visibility === 'roles' && !roleIds.length;
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="vx-btn primary" disabled={invalid} onClick={() => onConfirm({ visibility, role_ids: visibility === 'roles' ? roleIds : [] })} data-testid="link-visibility-confirm">
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="vxo-hint">Voxemera shows the file’s name and type to the people you choose. Opening the file still happens in Google Drive, which checks each person’s own access. Voxemera does not change the file’s sharing in Drive.</p>
      <fieldset className="vxo-fieldset">
        <legend>Who can see the link</legend>
        {Object.entries(VISIBILITY_TEXT).map(([k, label]) => (
          <label key={k} className="vxo-radio">
            <input type="radio" name="link-visibility" checked={visibility === k} onChange={() => setVisibility(k)} data-testid={`link-visibility-${k}`} />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>
      {visibility === 'roles' ? (
        roles === null ? (
          <Spinner />
        ) : roles.length ? (
          <fieldset className="vxo-fieldset">
            <legend>Roles</legend>
            {roles.map((r) => (
              <label key={r.id} className="vxo-check">
                <input type="checkbox" checked={roleIds.includes(Number(r.id))} onChange={(e) => setRoleIds((ids) => (e.target.checked ? [...ids, Number(r.id)] : ids.filter((x) => x !== Number(r.id))))} />
                <span>{r.name}</span>
              </label>
            ))}
          </fieldset>
        ) : (
          <p className="vxo-muted">This server has no roles to choose from.</p>
        )
      ) : null}
    </Dialog>
  );
}

// The explicit sharing workflow for the owner of a linked file. Voxemera never changes Drive sharing by itself.
export function DriveSharingDialog({ base, source, item, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [agree, setAgree] = useState(false);
  const shared = item.link && item.link.shared_by_link;
  async function run(method) {
    setBusy(true);
    setErr(null);
    try {
      const url = `${base}/library/sources/${source.id}/items/${item.vox_id}/share`;
      const r = method === 'POST' ? await apiPostJson(url, { confirm: 'anyone-with-link' }) : await apiDelete(url);
      notify(r.note, { tone: 'success' });
      onChanged();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={`Sharing “${item.name}” in Google Drive`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Close
          </button>
          {shared ? (
            <button type="button" className="vx-btn danger" disabled={busy} onClick={() => run('DELETE')} data-testid="drive-unshare">
              Stop link sharing
            </button>
          ) : (
            <button type="button" className="vx-btn primary" disabled={busy || !agree} onClick={() => run('POST')} data-testid="drive-share">
              Let anyone with the link view it
            </button>
          )}
        </>
      }
    >
      <ErrorNote error={err} />
      <p className="vxo-hint">
        Members open this file in Google Drive, and Google only lets in people the file is shared with. To let members open it, either share it with them in Google Drive yourself, or let Voxemera turn on Google’s “anyone with the link can view” setting for this one file.
      </p>
      <p>
        <a className="vx-btn small" href={item.web_url || '#'} target="_blank" rel="noopener noreferrer">
          Open in Google Drive to share with specific people
        </a>
      </p>
      {shared ? (
        <p className="vxo-warn-text">Link sharing is on: anyone who has the link, inside or outside this server, can view this file. Stopping it removes only the permission Voxemera added.</p>
      ) : (
        <label className="vxo-check">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="drive-share-agree" />
          <span>I understand that anyone who has the link, including people outside this server, will be able to view this file. They cannot edit it.</span>
        </label>
      )}
    </Dialog>
  );
}
